/**
 * @file Constantes du widget de réservation de table : étapes, statuts, types de
 * service, messages d'erreur et options de formatage de date/heure.
 * @module packages.common.components.table-reservation.constants.reservation
 */

// Reservation Step Constants
export const RESERVATION_STEPS = [
  {
    label: 'Service',
    icon: 'restaurant',
  },
  {
    label: 'Convives',
    icon: 'people',
  },
  {
    label: 'Date',
    icon: 'calendar',
  },
  {
    label: 'Horaire',
    icon: 'time',
  },
  {
    label: 'Coordonnées',
    icon: 'contact',
  },
];

// Availability indicators
export const AVAILABILITY_STATUS = {
  AVAILABLE: 'available',
  LIMITED: 'limited',
  BUSY: 'busy',
  UNAVAILABLE: 'unavailable',
  CLOSED: 'closed',
  SELECTED: 'selected',
  WAITLIST_ONLY: 'waitlist_only',
  WAITLIST_AVAILABLE: 'waitlist_available',
  // Date fermée en ligne par le préavis minimum : affichée comme disponible dans le
  // calendrier, mais la sélection déclenche l'invitation à réserver par téléphone
  // (statut purement interne, aucune légende dédiée).
  NOTICE_CLOSED: 'notice_closed',
  // Date complète (service bloqué ou plus aucune capacité) annoncée par l'API :
  // rendue « complet » même au-delà de l'horizon de réservation, pour que le
  // client n'attende pas l'ouverture d'un jour déjà plein.
  FULL: 'full',
};

// Service types
// Valeurs en français car elles correspondent aux identifiants de service côté backend.
export const SERVICE_TYPES = {
  LUNCH: 'midi',
  DINNER: 'soir',
};

// Statuts sous lesquels une réservation créée en ligne est considérée comme
// « confirmée » côté client. Tout autre statut renvoyé par le backend (typiquement
// `pending` quand l'établissement a désactivé la confirmation automatique) est
// présenté comme une demande en attente de validation par le restaurant.
export const CONFIRMED_RESERVATION_STATUSES = ['confirmed', 're_confirmed'];

// Error messages
export const ERROR_MESSAGES = {
  SERVICE_REQUIRED: 'Veuillez sélectionner un service',
  PARTY_SIZE_MIN: (min) => `Le nombre minimum de convives est de ${min} personnes`,
  PARTY_SIZE_MAX: (max) => `Le nombre maximum de convives est de ${max} personnes`,
  DATE_REQUIRED: 'Veuillez sélectionner une date',
  TIME_REQUIRED: 'Veuillez sélectionner un créneau horaire',
  FIRST_NAME_REQUIRED: 'Le prénom est requis',
  LAST_NAME_REQUIRED: 'Le nom est requis',
  EMAIL_REQUIRED: 'L\'email est requis',
  EMAIL_INVALID: 'Veuillez entrer une adresse email valide (ex: nom@domaine.com)',
  EMAIL_INVALID_FORMAT: 'L\'adresse email doit inclure un domaine valide (ex: .com, .fr)',
  PHONE_REQUIRED: 'Le numéro de téléphone est requis',
  PHONE_INVALID: 'Veuillez entrer un numéro de téléphone valide',
  ALLERGY_DETAILS_REQUIRED: 'Merci de préciser les allergies ou intolérances pour continuer.',
  MINIMUM_NOTICE: (hours) => `Les réservations doivent être faites au moins ${hours} heures à l'avance`,
  MAXIMUM_ADVANCE: (days) => `Les réservations sont possibles jusqu'à ${days} jours à l'avance`,
  NO_AVAILABILITY: 'Aucun créneau disponible pour cette date',
  GENERAL_ERROR: 'Une erreur s\'est produite. Veuillez réessayer plus tard.',
};

// Widget Configuration Errors
export const CONFIG_ERROR_TYPES = {
  MISSING_COMPANY_ID: 'MISSING_COMPANY_ID',
  MISSING_API_KEY: 'MISSING_API_KEY',
  MISSING_BOTH: 'MISSING_BOTH',
  INVALID_CONFIG: 'INVALID_CONFIG',
};

export const CONFIG_ERROR_MESSAGES = {
  [CONFIG_ERROR_TYPES.MISSING_COMPANY_ID]: {
    title: 'ID du restaurant manquant',
    message: 'L\'ID du restaurant n\'est pas spécifié.',
    details: 'Veuillez fournir un ID de restaurant valide.',
  },
  [CONFIG_ERROR_TYPES.MISSING_API_KEY]: {
    title: 'Clé API manquante',
    message: 'La clé API n\'est pas spécifiée.',
    details: 'Veuillez fournir une clé API valide.',
  },
  [CONFIG_ERROR_TYPES.MISSING_BOTH]: {
    title: 'Paramètres manquants',
    message: 'L\'ID du restaurant et la clé API sont manquants.',
    details: 'Ces informations sont nécessaires pour afficher le widget de réservation.',
  },
  [CONFIG_ERROR_TYPES.INVALID_CONFIG]: {
    title: 'Configuration incorrecte',
    message: 'Impossible de charger la configuration du restaurant.',
    details: 'Veuillez vérifier que l\'ID du restaurant et la clé API sont corrects.',
  },
};

// Date format options
export const DATE_FORMAT_OPTIONS = {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

// Time format
export const TIME_FORMAT = {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

/**
 * Codes renvoyés par le backend quand le créneau choisi n'est plus réservable
 * au moment de la création (409) : un autre client a pris la dernière place
 * (`SERVICE_FULL`) ou le restaurant a fermé ou modifié le service entre-temps
 * (`SLOT_UNAVAILABLE`). Le parcours affiche alors l'écran « créneau perdu ».
 */
export const SLOT_LOST_CODES = Object.freeze({
  SERVICE_FULL: 'SERVICE_FULL',
  SLOT_UNAVAILABLE: 'SLOT_UNAVAILABLE',
  // Codes du chemin de secours du backend (refus reconnus par leur message)
  LIMIT_REACHED: 'LIMIT_REACHED',
  AVAILABILITY_ERROR: 'AVAILABILITY_ERROR',
});

/** Codes qui signifient qu'un autre client a pris la dernière place */
const SERVICE_FULL_CODES = [SLOT_LOST_CODES.SERVICE_FULL, SLOT_LOST_CODES.LIMIT_REACHED];

/**
 * Indique si un code d'erreur de création correspond à un créneau perdu.
 *
 * @param {string} code - Code renvoyé par le backend
 * @return {boolean} true si l'écran « créneau perdu » doit s'afficher
 */
export function isSlotLostCode(code) {
  return Object.values(SLOT_LOST_CODES).includes(code);
}

/**
 * Indique si le créneau a été perdu parce que le service est complet.
 *
 * @param {string} code - Code renvoyé par le backend
 * @return {boolean} true pour un service complet, false pour un créneau fermé ou modifié
 */
export function isServiceFullCode(code) {
  return SERVICE_FULL_CODES.includes(code);
}
